import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { IoArrowBack, IoBag } from 'react-icons/io5'

import Input from '@/components/forms/Input/Input'
import Button from '@/components/common/Button/Button'
import Spinner from '@/components/common/Spinner/Spinner'
import routes from '@/routes/routes'
import useAuthStore, { type ResetPasswordRequest } from '@/stores/AuthStore'

import './ForgotPassword.css'

const ForgotPassword = () => {
    const navigate = useNavigate()
    const { sendResetPasswordRequest, isLoading, isLogged } = useAuthStore()

    const [formData, setFormData] = useState<ResetPasswordRequest>({
        email: '',
    })

    useEffect(() => {
        if (isLogged) {
            navigate(routes.home)
        }
    }, [isLogged])

    const handleFormDataChange = (key: string, value: string) => {
        setFormData((prevState) => ({
            ...prevState,
            [key]: value,
        }))
    }

    const validateForm = (formData: ResetPasswordRequest) => {
        const { email } = formData
        
        if (!email) {
            return { isValid: false, message: 'Por favor ingrese su dirección de correo electrónico' }
        }
        
        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(email)) {
            return { isValid: false, message: 'Por favor ingrese una dirección de correo electrónico válida' }
        }
        
        return { isValid: true, message: '' }
    }

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        const validation = validateForm(formData)
        if (!validation.isValid) {
            toast.error(validation.message)
            return
        }

        try {
            const status: boolean = await sendResetPasswordRequest(formData)
            if (status) {
                toast.success('¡Enlace de restablecimiento de contraseña enviado! Por favor revise su correo electrónico.')
                navigate(routes.login)
                return
            }
            toast.error('Error al enviar el enlace de restablecimiento. Por favor inténtelo de nuevo.')
        } catch (error) {
            toast.error('Error al enviar el enlace de restablecimiento. Por favor inténtelo de nuevo.')
            console.error('Reset password error:', error)
        }
    }

    return (
        <div className="auth_page">
            <div className="auth_container">
                <div className="auth_header">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => navigate(routes.login)}
                    >
                        <IoArrowBack size={16} />
                        Volver al Inicio de Sesión
                    </Button>
                </div>

                <div className="auth_card">
                    <div className="auth_brand">
                        <div className="brand_logo">
                            <IoBag size={32} />
                        </div>
                        <h1>Restablecer Contraseña</h1>
                        <p>Ingrese su dirección de correo electrónico y le enviaremos un enlace para restablecer su contraseña</p>
                    </div>

                    <form className="auth_form" onSubmit={onSubmit}>
                        <Input
                            name="email"
                            value={formData.email}
                            setValue={(value) => handleFormDataChange('email', value)}
                            label="Dirección de Correo Electrónico"
                            placeholder="Ingrese su correo electrónico"
                            type="email"
                            required
                        />

                        <Button
                            variant="primary"
                            size="lg"
                            type="submit"
                            disabled={isLoading}
                            className="auth_submit"
                        >
                            {isLoading ? <Spinner size="sm" variant="secondary" /> : 'Enviar Enlace de Restablecimiento'}
                        </Button>
                    </form>

                    <div className="auth_footer">
                        <p>
                            ¿Recuerda su contraseña?{' '}
                            <button 
                                type="button"
                                className="auth_link"
                                onClick={() => navigate(routes.login)}
                            >
                                Iniciar Sesión
                            </button>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ForgotPassword
